import { adaptEWarrantListResponse } from './eWarrant.adapter';
import {
  EWarrant,
  EWarrantActionTypes,
  EWarrantState,
  GENERATE_EWARRANT_FULFILLED,
  GET_EWARRANT_DETAIL_FULFILLED,
  GET_EWARRANT_DETAIL_PENDING,
  GET_EWARRANT_DETAIL_REJECTED,
  GET_EWARRANT_LIST_FULFILLED,
  GET_EWARRANT_LIST_PENDING,
  GET_EWARRANT_LIST_REJECTED,
  SELECT_EWARRANT,
} from './eWarrant.types';

/**
 * The e-warrants a list read brings back, folded into the ones already held. The grid
 * renders from the server rather than from here, so what this list is for is resolving
 * the e-warrant behind a selected row: the fresh page wins for the rows it carries, and
 * a row from an earlier page is kept so selecting it after paging back still resolves.
 */
const mergeEWarrantPage = (held: EWarrant[], page: EWarrant[]): EWarrant[] => [
  ...page,
  ...held.filter(({ id }) => !page.some((fresh) => fresh.id === id)),
];

export const initialState: EWarrantState = {
  entities: [],
  selected: [],
  ui: {
    isLoading: false
  }
};

export default function eWarrantReducer(
  state = initialState,
  action: EWarrantActionTypes,
): EWarrantState {
  switch (action.type) {
    case GET_EWARRANT_LIST_PENDING:
      return {
        ...state,
        selected: [],
        ui: {
          ...state.ui,
          isLoading: true
        }
      };

    case GET_EWARRANT_LIST_FULFILLED:
      return {
        ...state,
        entities: mergeEWarrantPage(
          state.entities,
          adaptEWarrantListResponse(action.payload?.data?.data ?? []),
        ),
        selected: [],
        ui: {
          ...state.ui,
          isLoading: false
        }
      };

    case GET_EWARRANT_LIST_REJECTED:
      return {
        ...state,
        ui: {
          ...state.ui,
          isLoading: false
        }
      };

    case GET_EWARRANT_DETAIL_PENDING:
      return {
        ...state,
        ui: {
          ...state.ui,
          isLoading: true
        }
      };

    // Merged onto the entity the list produced, not swapped in for it: the grid is
    // driven off the list read, not off this one, so nothing here should replace an
    // entity the list is still the source of truth for.
    case GET_EWARRANT_DETAIL_FULFILLED:
      return {
        ...state,
        entities: state.entities.map((eWarrant) => (
          eWarrant.id === action.payload.id
            ? { ...eWarrant, ...action.payload }
            : eWarrant
        )),
        ui: {
          ...state.ui,
          isLoading: false
        }
      };

    case GET_EWARRANT_DETAIL_REJECTED:
      return {
        ...state,
        ui: {
          ...state.ui,
          isLoading: false
        }
      };

    case SELECT_EWARRANT:
      return {
        ...state,
        selected: action.payload ?? []
      };

    case GENERATE_EWARRANT_FULFILLED:
      return {
        ...state,
        entities: state.entities.map((eWarrant) => (
          eWarrant.id === action.payload.id
            ? { ...eWarrant, documents: [...eWarrant.documents, action.payload.document] }
            : eWarrant
        ))
      };

    default:
      return state;
  }
}
